/**
 * ConnectionErrorToast - Error notification for connection validation failures
 *
 * Displays error messages when connections are rejected by validation rules.
 * Auto-dismisses after 3000ms (ui_constraints.error_toast_duration).
 *
 * Related: FR-007, FR-008 - Display error messages for invalid connections
 */

import React from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface ConnectionErrorToastProps {
  error: string | null;
  onClose: () => void;
}

/**
 * Toast notification component for connection validation errors
 *
 * Features:
 * - Auto-dismiss after 3000ms
 * - MUI Snackbar + Alert components
 * - Theme-only styling (no hardcoded colors)
 * - Error severity with theme.palette.error colors
 */
export const ConnectionErrorToast: React.FC<ConnectionErrorToastProps> = ({
  error,
  onClose
}) => {
  const theme = useTheme();

  return (
    <Snackbar
      open={Boolean(error)}
      autoHideDuration={3000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={onClose}
        severity="error"
        sx={{
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        }}
      >
        {error}
      </Alert>
    </Snackbar>
  );
};
